import moment from 'moment'
import { Link } from 'react-router-dom'
import { ChatBubbleLeftRightIcon, PlusIcon } from '@heroicons/react/24/outline'

/** Data rozmowy (data i godzina rozmowy). */
const getDigRozmowy = item =>
	item?.dig_rozmowy ?? item?.added ?? item?.created ?? item?.date ?? null

/** Planowana data następnej rozmowy. */
const getDigNastepnejRozmowy = item => item?.dig_nastepnej_rozmowy ?? null

const formatDateTime = value => (value ? moment(value).format('YYYY-MM-DD HH:mm') : null)

const getImieNazwisko = item => {
	if (item?.imie_i_nazwisko) return item.imie_i_nazwisko
	if (typeof item?.id_osoba === 'object' && item?.id_osoba?.imie_i_nazwisko) {
		return item.id_osoba.imie_i_nazwisko
	}
	return '-'
}

const getPrzeprowadzil = item => {
	const u = item?.id_user
	if (u == null || u === '') return '-'
	if (typeof u === 'object') {
		return (
			u.nazwa || '-'
		)
	}
	return String(u) || '-'
}

/** Treść feedbacku: obiekt feedback.tresc lub pole tresc / legacy note. */
const getFeedbackTresc = item => {
	const f = item?.feedback
	if (f != null) {
		if (typeof f === 'object') return f?.tresc ?? ''
		if (typeof f === 'string') return f
	}
	return item?.tresc ?? item?.note ?? item?.message ?? item?.description ?? ''
}

const getRelationId = value => {
	if (!value) return null
	if (typeof value === 'object') return value?._id || value?.id || null
	return value
}

export default function CommunicationHistoryCard({ rozmowy = [], placowka = null, osoba = null }) {
	const placowkaId = getRelationId(placowka) || getRelationId(osoba?.id_placowka)
	const osobaId = getRelationId(osoba)
	const addParams = new URLSearchParams()
	if (placowkaId) addParams.set('id_placowka', String(placowkaId))
	if (osobaId) addParams.set('id_osoba', String(osobaId))
	const addHref = addParams.toString() ? `/komunikacja/rozmowy/add?${addParams.toString()}` : null
	const rozmowyRows = Array.isArray(rozmowy) ? rozmowy : []
	const sorted = [...rozmowyRows].sort((a, b) => {
		const da = getDigRozmowy(a) ? new Date(getDigRozmowy(a)).getTime() : 0
		const db = getDigRozmowy(b) ? new Date(getDigRozmowy(b)).getTime() : 0
		return db - da
	})

	return (
		<li className="col-span-1">
			<div className="flex h-[440px] w-full flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
				<div className="flex items-center justify-between gap-3 border-b border-gray-300 bg-gray-100 px-6 py-3">
					<h2 className="flex items-center gap-2 text-md font-semibold text-gray-900">
						<ChatBubbleLeftRightIcon className="h-5 w-5 text-sky-700" />
						<span>Historia komunikacji</span>
					</h2>
					{addHref ? (
						<Link
							to={addHref}
							className="inline-flex items-center gap-1.5 rounded-md bg-sky-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-800"
						>
							<PlusIcon className="h-4 w-4" />
							<span>{osobaId ? 'Dodaj rozmowe' : 'Dodaj'}</span>
						</Link>
					) : null}
				</div>

				<div className="flex-1 overflow-y-auto px-3 py-3 sm:px-4">
					{sorted.length === 0 ? (
						<div className="flex min-h-[155px] items-center justify-center gap-3 rounded-md bg-white px-4 pt-5 text-sm text-slate-600">
							<ChatBubbleLeftRightIcon className="h-5 w-5 text-slate-400" />
							<span>Brak historii komunikacji.</span>
						</div>
					) : (
						<div className="relative space-y-5">
							<div className="absolute left-5 top-0 h-full w-px bg-gray-200" aria-hidden="true" />
							{sorted.map((rozmowa, index) => {
								const rozmowaId = rozmowa?._id || rozmowa?.id
								const key = rozmowaId || `${getDigRozmowy(rozmowa) || 'na'}-${index}`
								const dig = getDigRozmowy(rozmowa)
								const digNastepna = getDigNastepnejRozmowy(rozmowa)
								const feedbackText = getFeedbackTresc(rozmowa)
								const lineRozmowy = formatDateTime(dig)
								const lineNastepna = formatDateTime(digNastepna)
								const cardClassName =
									'w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm'
								const cardBody = (
									<>
										<p className="text-sm font-semibold text-gray-900">
											Osoba: {getImieNazwisko(rozmowa)}
										</p>
										<p className="mt-0.5 text-sm font-semibold text-gray-900">
											Przeprowadził: {getPrzeprowadzil(rozmowa)}
										</p>
										<p className="mt-1 text-xs text-gray-500">
											{lineRozmowy || '-'}
											{lineNastepna ? (
												<span>
													{' '}
													(następna: {lineNastepna})
												</span>
											) : null}
										</p>
										<p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
											{feedbackText ? feedbackText : '-'}
										</p>
									</>
								)
								return (
									<div key={key} className="relative flex items-start gap-4 pl-12">
										<div className="absolute left-1 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-blue-600 shadow-sm">
											<ChatBubbleLeftRightIcon className="h-5 w-5" />
										</div>
										{rozmowaId ? (
											<Link
												to={`/komunikacja/rozmowy/details/${rozmowaId}`}
												className={`${cardClassName} block cursor-pointer transition hover:border-sky-400 hover:bg-sky-50`}
											>
												{cardBody}
											</Link>
										) : (
											<div className={cardClassName}>{cardBody}</div>
										)}
									</div>
								)
							})}
						</div>
					)}
				</div>
			</div>
		</li>
	)
}
